import type { BaseQueryFn, BaseQueryApi } from '@reduxjs/toolkit/query'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { signinSilent } from 'Auth/shared/silentRenew'
import { getAccessToken } from 'axiosConfig'

const prepareHeaders = (
  headers: Headers,
  _api: Pick<
    BaseQueryApi,
    'getState' | 'extra' | 'endpoint' | 'type' | 'forced'
  >
) => {
  const token = getAccessToken()
  if (!headers.get('Authorization') && token) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  if (!headers.has('x-api-version')) {
    headers.set('x-api-version', '1.0')
  }

  headers.set(
    'Ocp-Apim-Subscription-Key',
    window.config?.api?.subscription ?? ''
  )

  headers.set('APIM-BBLCode', window.config?.bblCode ?? '')
  return headers
}

export const fillIfNull = (
  params: Record<string, unknown>,
  keys: string[],
  value: string
) => {
  const key = keys.find((k) => k in params && params[k] == null)
  return key ? { [key]: value } : {}
}

export const injectClientId = (
  args: Record<string, unknown>,
  clientId: string
) => {
  if (typeof args.url === 'string') {
    args = { ...args, url: args.url.replace(':clientId', clientId) }
  }

  if (!args.params || typeof args.params !== 'object') return args

  const params = args.params as Record<string, unknown>
  return {
    ...args,
    params: {
      ...params,
      ...fillIfNull(params, ['clientId', 'ClientId'], clientId),
    },
  }
}

const innerQuery = fetchBaseQuery({
  baseUrl: window.config?.api?.url ?? '/',
  prepareHeaders,
})

export const baseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const clientId =
    (api.getState() as { clientStore: { currentId: string | null } })
      .clientStore.currentId ?? ''

  const hasArguments = typeof args === 'object' && args !== null

  if (hasArguments) {
    args = injectClientId(args, clientId)
  }

  const tokenUsed = getAccessToken()
  let result = await innerQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    const argsObj = hasArguments ? { ...args } : {}
    const isRetry = argsObj._retry

    if (!isRetry) {
      argsObj._retry = true

      const currentToken = getAccessToken()

      const canRetry = currentToken && currentToken !== tokenUsed

      if (canRetry) {
        return innerQuery(argsObj, api, extraOptions)
      }

      // WebView auth has no OIDC session, silent renew cannot recover.
      if (!window.ReactNativeWebView) {
        try {
          const user = await signinSilent()
          if (user?.access_token) {
            result = await innerQuery(argsObj, api, extraOptions)
          }
        } catch {
          // Fallback to original error
        }
      }
    }
  }

  return result
}
